import React, { ForwardRefRenderFunction, forwardRef } from "react";
//#region types
import { TimeReportingProjectMemberFormContainerProps } from "models/propTypes/timeReportingProjectPropTypes";
import { FormHandles } from "models/propTypes/genericFormProps";
import { TimeReportingProjectFormTypes } from "models/formTypes";
import { TranslationKeys } from "translations";
import { WintSuperkollAPIModelTimeReporting as ApiModel } from "wint-api-client/dist/types";
import { FormFieldConstants, TimeReportingConstants } from "utils/constants";
import { FormikProps } from "formik";
import { AutocompleteListItem } from "models/propTypes/listPropTypes";
//#endregion
//#region components
import TimeReportingProjectMemberForm from "./timeReportingProjectMemberForm";
import { ExtendedProjectCategory, ProjectCategory } from "models/formTypes/timeReportingProject";
import { useTimeReportingProjectService } from "serviceHooks";
//#endregion
import * as Yup from "yup";
import { DateHelper, FormHelper, LocalizationHelperInstance, RegularExpressions } from "utils/helpers";

type SelfBillingAccountField =
  | "selfBillingBankgiro"
  | "selfBillingPlusgiro"
  | "selfBillingIBAN"
  | "selfBillingClearingNumber"
  | "selfBillingAccountNumber";

const selfBillingAccountFieldsByPaymentMethod: Partial<
  Record<ApiModel.SelfBillingPaymentMethod, SelfBillingAccountField[]>
> = {
  [ApiModel.SelfBillingPaymentMethod.Bankgiro]: ["selfBillingBankgiro"],
  [ApiModel.SelfBillingPaymentMethod.Plusgiro]: ["selfBillingPlusgiro"],
  [ApiModel.SelfBillingPaymentMethod.IBAN]: ["selfBillingIBAN"],
  [ApiModel.SelfBillingPaymentMethod.BankTransfer]: ["selfBillingClearingNumber", "selfBillingAccountNumber"],
};

// Each payment method owns its own account fields, but a hidden field keeps whatever an
// earlier selection wrote and Core stores the configuration verbatim. Without this, a member
// switched from Bankgiro to Plusgiro is saved with both numbers set, and the two share the
// same NNNNNNN-N surface form. This runs on submit rather than on chip change: the chip
// field writes its label before the id lands, which resets the form container's user-input
// flag, so a change handler never sees the id as user input.
const stripInactiveSelfBillingAccountFields = (
  member: TimeReportingProjectFormTypes.ProjectMember
): TimeReportingProjectFormTypes.ProjectMember => {
  const keptFields =
    selfBillingAccountFieldsByPaymentMethod[member.selfBillingPaymentMethod as ApiModel.SelfBillingPaymentMethod];
  if (!keptFields) {
    return member;
  }

  const cleaned = { ...member };
  Object.values(selfBillingAccountFieldsByPaymentMethod)
    .flatMap((fields) => fields ?? [])
    .filter((field) => !keptFields.includes(field))
    .forEach((field) => {
      cleaned[field] = undefined;
    });
  return cleaned;
};

const TimeReportingProjectMemberFormContainer: ForwardRefRenderFunction<
  FormHandles,
  TimeReportingProjectMemberFormContainerProps
> = (props, ref) => {
  const timeReportingProjectService = useTimeReportingProjectService();
  const { excludeMemberIds, isInternalProject, project } = props;

  const earliestStartDate = new Date(project.projectStartDate);
  const latestEndDate = project.projectEndDate ?? DateHelper.getDateMax();

  const formRef = React.useRef<FormHandles>(null);
  React.useImperativeHandle(ref, () => (formRef?.current ? formRef.current : ({} as FormHandles)));

  // A member added in this session is handed back with isNew: false and the person's id, so isNew
  // cannot tell us whether a rate change has to be queued from a month — only rates the API has
  // already stored carry an id. Without a stored rate there is nothing to change from.
  const hasSelfBillingRateHistory = (props.selfBillingAllHourlyRates ?? props.selfBillingHourlyRates ?? []).some(
    (rate) => rate.id !== undefined
  );

  const validationSchema = Yup.object<TimeReportingProjectFormTypes.ProjectMember>({
    name: Yup.string().required(TranslationKeys.Validations.Required),
    id: Yup.number().required(),
    startDate: Yup.date()
      .typeError(TranslationKeys.Validations.InvalidDate)
      .required(TranslationKeys.Validations.Required)
      // eslint-disable-next-line no-empty-pattern
      .when([], ([], schema: Yup.DateSchema, node: any) => {
        return (node.value as Date) >= earliestStartDate
          ? schema
          : schema
              .typeError(TranslationKeys.Validations.InvalidDate)
              .required(TranslationKeys.Validations.Required)
              .test("validatedate", TranslationKeys.TimeReportingProject.MemberDatesareNotWithinProjectDates, (val) => {
                return (node.value as Date) >= earliestStartDate;
              });
      }),
    endDate: Yup.date().typeError(TranslationKeys.Validations.InvalidDate),
    isNew: Yup.boolean(),
    isTimeReportingActive: Yup.boolean(),
    changesApplyFrom: Yup.date().when(
      FormFieldConstants.TimeReportingProject.ProjectMember.CategoryRates,
      ([categoryRates], schema: Yup.DateSchema) => {
        return !(props.id === undefined) &&
          categoryRates?.some((cr: TimeReportingProjectFormTypes.CategoryRate) => {
            const initialCategoryRate = initialValues.categoryRates?.find((icr) => icr.name === cr.name);
            if (!initialCategoryRate) {
              return false;
            }
            return initialCategoryRate.hourlyRate !== cr.hourlyRate;
          })
          ? schema.required(TranslationKeys.Validations.Required)
          : schema;
      }
    ),
    categoryRates: Yup.array()
      .of(
        Yup.object<TimeReportingProjectFormTypes.CategoryRate>({
          id: Yup.number(),
          billingModel: Yup.number(),
          isDefault: Yup.boolean(),
          hours: Yup.number().when("individualBillingModel", ([individualBillingModel], schema: Yup.NumberSchema) =>
            individualBillingModel !== ApiModel.TimeReportingPersonProjectBillingModel.Hourly
              ? schema.required(TranslationKeys.Validations.Required).moreThan(0)
              : schema
          ),
          shouldEffectSalaryPayout: Yup.boolean(),
          adjustPreviouslyReportedTime: Yup.boolean(),
        })
      )
      .uniqueProperties(
        (item: TimeReportingProjectFormTypes.CategoryRate) =>
          JSON.stringify({
            data:
              item.individualBillingModel === ApiModel.TimeReportingPersonProjectBillingModel.FixedPriceMinusDeviations
                ? true
                : item.name,
          }) as any,
        LocalizationHelperInstance.getFormattedMessage(
          TranslationKeys.TimeReportingProject.OnlyOneFixedPriceMinusDeviationForPersonProject
        )
      ),
    futureCategoryRates: Yup.array()
      .of(
        Yup.object<TimeReportingProjectFormTypes.FutureCategoryRate>({
          id: Yup.number(),
          validFrom: Yup.date(),
          isDefault: Yup.boolean(),
          billingModel: Yup.number(),
          shouldEffectSalaryPayout: Yup.boolean(),
          adjustPreviouslyReportedTime: Yup.boolean(),
        })
      )
      .uniqueProperties(
        (item: TimeReportingProjectFormTypes.FutureCategoryRate) =>
          JSON.stringify({ validFrom: item.validFrom, categoryName: item.name }) as any,
        LocalizationHelperInstance.getFormattedMessage(
          TranslationKeys.TimeReportingProject.AddedHourlyRateAlreadyExistsForMonth
        )
      )
      .uniqueProperties(
        (item: TimeReportingProjectFormTypes.FutureCategoryRate) =>
          JSON.stringify({
            current: item.validFrom && DateHelper.isBefore(item.validFrom, new Date()) ? new Date() : item.validFrom,
            categoryName: item.name,
          }) as any,
        LocalizationHelperInstance.getFormattedMessage("more than one month before today")
      ),
    selfBillingEnabled: Yup.boolean(),
    selfBillingOrgNumber: Yup.string().when(
      FormFieldConstants.TimeReportingProject.ProjectMember.SelfBillingEnabled,
      ([selfBillingEnabled], schema: Yup.StringSchema) =>
        selfBillingEnabled
          ? schema.required(TranslationKeys.Validations.Required).matches(RegularExpressions.OrgNr, {
              message: TranslationKeys.Validations.InvalidOrgNumber,
              excludeEmptyString: true,
            })
          : schema
    ),
    selfBillingEmail: Yup.string().when(
      FormFieldConstants.TimeReportingProject.ProjectMember.SelfBillingEnabled,
      ([selfBillingEnabled], schema: Yup.StringSchema) =>
        selfBillingEnabled
          ? schema.required(TranslationKeys.Validations.Required).email(TranslationKeys.Validations.InvalidEmail)
          : schema
    ),
    selfBillingPaymentMethod: Yup.number().when(
      FormFieldConstants.TimeReportingProject.ProjectMember.SelfBillingEnabled,
      ([selfBillingEnabled], schema: Yup.NumberSchema) =>
        selfBillingEnabled ? schema.required(TranslationKeys.Validations.Required) : schema
    ),
    selfBillingBankgiro: Yup.string().when(
      [
        FormFieldConstants.TimeReportingProject.ProjectMember.SelfBillingEnabled,
        FormFieldConstants.TimeReportingProject.ProjectMember.SelfBillingPaymentMethod,
      ],
      ([selfBillingEnabled, paymentMethod], schema: Yup.StringSchema) =>
        selfBillingEnabled && paymentMethod === ApiModel.SelfBillingPaymentMethod.Bankgiro
          ? schema.max(20, TranslationKeys.Validations.ValueTooLong).required(TranslationKeys.Validations.Required)
          : schema
    ),
    selfBillingPlusgiro: Yup.string().when(
      [
        FormFieldConstants.TimeReportingProject.ProjectMember.SelfBillingEnabled,
        FormFieldConstants.TimeReportingProject.ProjectMember.SelfBillingPaymentMethod,
      ],
      ([selfBillingEnabled, paymentMethod], schema: Yup.StringSchema) =>
        selfBillingEnabled && paymentMethod === ApiModel.SelfBillingPaymentMethod.Plusgiro
          ? schema.max(20, TranslationKeys.Validations.ValueTooLong).required(TranslationKeys.Validations.Required)
          : schema
    ),
    selfBillingClearingNumber: Yup.string().when(
      [
        FormFieldConstants.TimeReportingProject.ProjectMember.SelfBillingEnabled,
        FormFieldConstants.TimeReportingProject.ProjectMember.SelfBillingPaymentMethod,
      ],
      ([selfBillingEnabled, paymentMethod], schema: Yup.StringSchema) =>
        selfBillingEnabled && paymentMethod === ApiModel.SelfBillingPaymentMethod.BankTransfer
          ? schema
              .matches(/^[0-9]{4,5}$/, {
                message: TranslationKeys.Validations.InvalidClearingNumber,
                excludeEmptyString: true,
              })
              .required(TranslationKeys.Validations.Required)
          : schema
    ),
    selfBillingAccountNumber: Yup.string().when(
      [
        FormFieldConstants.TimeReportingProject.ProjectMember.SelfBillingEnabled,
        FormFieldConstants.TimeReportingProject.ProjectMember.SelfBillingPaymentMethod,
      ],
      ([selfBillingEnabled, paymentMethod], schema: Yup.StringSchema) =>
        selfBillingEnabled && paymentMethod === ApiModel.SelfBillingPaymentMethod.BankTransfer
          ? schema
              .matches(/^[0-9]{7,10}$/, {
                message: TranslationKeys.Validations.InvalidBankAccount,
                excludeEmptyString: true,
              })
              .required(TranslationKeys.Validations.Required)
          : schema
    ),
    selfBillingIBAN: Yup.string().when(
      [
        FormFieldConstants.TimeReportingProject.ProjectMember.SelfBillingEnabled,
        FormFieldConstants.TimeReportingProject.ProjectMember.SelfBillingPaymentMethod,
      ],
      ([selfBillingEnabled, paymentMethod], schema: Yup.StringSchema) =>
        selfBillingEnabled && paymentMethod === ApiModel.SelfBillingPaymentMethod.IBAN
          ? schema.required(TranslationKeys.Validations.Required)
          : schema
    ),
    selfBillingPaymentTerms: Yup.number().when(
      FormFieldConstants.TimeReportingProject.ProjectMember.SelfBillingEnabled,
      ([selfBillingEnabled], schema: Yup.NumberSchema) =>
        selfBillingEnabled ? schema.required(TranslationKeys.Validations.Required) : schema
    ),
    selfBillingCalculationMethod: Yup.number().when(
      FormFieldConstants.TimeReportingProject.ProjectMember.SelfBillingEnabled,
      ([selfBillingEnabled], schema: Yup.NumberSchema) =>
        selfBillingEnabled ? schema.required(TranslationKeys.Validations.Required) : schema
    ),
    selfBillingPercentage: Yup.number().when(
      [
        FormFieldConstants.TimeReportingProject.ProjectMember.SelfBillingEnabled,
        FormFieldConstants.TimeReportingProject.ProjectMember.SelfBillingCalculationMethod,
      ],
      ([selfBillingEnabled, calculationMethod], schema: Yup.NumberSchema) =>
        selfBillingEnabled && calculationMethod === 1
          ? schema
              .min(0, TranslationKeys.Validations.Min)
              .max(100, TranslationKeys.Validations.Max)
              .required(TranslationKeys.Validations.Required)
          : schema
    ),
    selfBillingHourlyRates: Yup.array().when(
      [
        FormFieldConstants.TimeReportingProject.ProjectMember.SelfBillingEnabled,
        FormFieldConstants.TimeReportingProject.ProjectMember.SelfBillingCalculationMethod,
      ],
      ([selfBillingEnabled, calculationMethod]: [boolean, number], schema: any) =>
        selfBillingEnabled && calculationMethod === ApiModel.SelfBillingCalculationMethod.HourlyRate
          ? schema
              .of(
                Yup.object<TimeReportingProjectFormTypes.SelfBillingRate>({
                  id: Yup.number(),
                  categoryName: Yup.string().required(),
                  amount: Yup.number()
                    .required(TranslationKeys.Validations.Required)
                    .moreThan(0, TranslationKeys.Validations.Required),
                })
              )
              .min(1, TranslationKeys.Validations.Required)
          : schema
    ),
    selfBillingAllHourlyRates: Yup.array(),
    selfBillingFutureRates: Yup.array(),
    selfBillingChangesApplyFrom: Yup.date().when(
      [
        FormFieldConstants.TimeReportingProject.ProjectMember.SelfBillingEnabled,
        FormFieldConstants.TimeReportingProject.ProjectMember.SelfBillingCalculationMethod,
        FormFieldConstants.TimeReportingProject.ProjectMember.SelfBillingHourlyRates,
      ],
      ([selfBillingEnabled, calculationMethod, rates]: any[], schema: Yup.DateSchema) => {
        const changedRates = rates as TimeReportingProjectFormTypes.SelfBillingRate[] | undefined;
        // A category that had no rate before is a first rate, not a change, and needs no month.
        const amountChanged =
          hasSelfBillingRateHistory &&
          selfBillingEnabled &&
          calculationMethod === ApiModel.SelfBillingCalculationMethod.HourlyRate &&
          changedSelfBillingRows(changedRates).length > 0;
        return amountChanged ? schema.required(TranslationKeys.Validations.Required) : schema;
      }
    ),
    selfBillingMonthlyCost: Yup.number().when(
      [
        FormFieldConstants.TimeReportingProject.ProjectMember.SelfBillingEnabled,
        FormFieldConstants.TimeReportingProject.ProjectMember.SelfBillingCalculationMethod,
      ],
      ([selfBillingEnabled, calculationMethod], schema: Yup.NumberSchema) =>
        selfBillingEnabled && calculationMethod === 3 ? schema.required(TranslationKeys.Validations.Required) : schema
    ),
  });

  const mapExtendedCategories = (
    projectCategoryMembers: ApiModel.ProjectCategoryMemberRelation[],
    projectCategories: ProjectCategory[],
    projectMemberId: number
  ) => {
    const mappedExtendedCategories = projectCategories.map((pc) => {
      const projectCategoryMemberRelation: ApiModel.ProjectCategoryMemberRelation | undefined =
        projectCategoryMembers.find(
          (pcm: ApiModel.ProjectCategoryMemberRelation) =>
            // eslint-disable-next-line eqeqeq --- supressed to avoid regression testing if changed
            pcm.projectMember?.id === projectMemberId && pc.name == pcm.projectCategory?.name
        );

      let isSelected = props.isNew ?? true;

      if (projectCategoryMemberRelation) {
        isSelected = projectCategoryMemberRelation?.endDate
          ? DateHelper.isBefore(DateHelper.today(), new Date(projectCategoryMemberRelation?.endDate))
          : true;
      }

      return {
        ...pc,
        selected: isSelected,
        accessUntil: projectCategoryMemberRelation?.endDate,
        hourlyRate:
          projectCategoryMemberRelation?.hourlyRates?.find((hr) => hr.isCurrent)?.hourlyRate ?? pc.defaultHourlyRate,
        individualBillingModel: projectCategoryMemberRelation?.individualBillingModel ?? 0,
        hours: projectCategoryMemberRelation?.normalDebitHours,
        currentRate: projectCategoryMemberRelation?.hourlyRates?.find((hr) => hr.isCurrent)?.hourlyRate ?? 0,
        shouldEffectSalaryPayout: projectCategoryMemberRelation?.projectCategory?.shouldEffectSalaryPayout,
      } as ExtendedProjectCategory;
    });

    return mappedExtendedCategories;
  };

  const mapCategoryRates = () => {
    if (props.isNew ?? true) {
      return project?.projectCategories?.map(
        (pc) =>
          ({
            ...pc,
            selected: true,
            hourlyRate: pc.defaultHourlyRate,
            hours: 0,
            individualBillingModel: 0,
            individualBillingModelName: timeReportingProjectService.getMemberBillingModelText(0),
            id: 0,
            shouldEffectSalaryPayout: false,
          }) as TimeReportingProjectFormTypes.CategoryRate
      );
    }

    return project?.projectCategoryMembers
      ?.filter(
        (pcm) =>
          pcm.projectMember?.id === props.id &&
          !(pcm.endDate && DateHelper.isBefore(new Date(pcm.endDate), DateHelper.today()))
      )
      .map((pcm) => {
        return {
          ...pcm.projectCategory,
          selected: false,
          hours: pcm.normalDebitHours,
          individualBillingModel: pcm.individualBillingModel,
          individualBillingModelName: timeReportingProjectService.getMemberBillingModelText(pcm.individualBillingModel),
          hourlyRate: pcm.hourlyRates?.find((hr) => hr.isCurrent)?.hourlyRate ?? 0,
          currentRate: pcm.hourlyRates?.find((hr) => hr.isCurrent)?.hourlyRate ?? 0,
        } as TimeReportingProjectFormTypes.CategoryRate;
      });
  };

  const mapFutureCategoryRates = () => {
    return project?.projectCategoryMembers
      ?.filter((pcm) => pcm.projectMember?.id === props.id && !!pcm.hourlyRates)
      .flatMap((pcm) => pcm.hourlyRates?.filter((hr) => !hr.isCurrent).map((hr) => ({ ...hr, pcm: pcm })))
      .map(
        (hr) =>
          ({
            ...hr?.pcm?.projectCategory,
            hourlyRateId: hr?.id,
            hourlyRate: hr?.hourlyRate,
            hours: hr?.pcm.normalDebitHours,
            validFrom: hr?.fromDate,
            individualBillingModel: hr?.pcm.individualBillingModel,
            currentRate: hr?.pcm.hourlyRates?.find((pcmHourlyRate) => pcmHourlyRate.isCurrent)?.hourlyRate ?? 0,
          }) as TimeReportingProjectFormTypes.FutureCategoryRate
      )
      .filter((hr) => (!!hr.validFrom && DateHelper.isBefore(new Date(), new Date(hr.validFrom))) || !hr.hourlyRateId);
  };

  // A category can now hold several rates; the editable table shows the one in force today.
  // The API decides which that is and marks it isCurrent. Rows built locally for a category
  // added in this session have no date and no flag yet, so they are the original rate.
  // A category whose rates all start in the future has none in force, and shows blank.
  const pickCurrentRate = (rows: TimeReportingProjectFormTypes.SelfBillingRate[]) =>
    rows.find((r) => r.isCurrent) ?? rows.find((r) => !r.fromDate);

  const mapSelfBillingRates = (
    existingRates?: TimeReportingProjectFormTypes.SelfBillingRate[],
    categories?: TimeReportingProjectFormTypes.ExtendedProjectCategory[]
  ) => {
    // Use selected categories from the member, or fall back to all project categories
    const categoriesToUse = categories?.filter((c) => c.selected) ?? project?.projectCategories ?? [];

    if (!categoriesToUse || categoriesToUse.length === 0) {
      return [] as TimeReportingProjectFormTypes.SelfBillingRate[];
    }

    return categoriesToUse.map((category) => {
      const rowsForCategory = existingRates?.filter((r) => r.timeReportingProjectCategoryId === category.id) ?? [];
      const current = pickCurrentRate(rowsForCategory);
      return {
        id: current?.id,
        timeReportingProjectCategoryId: category.id ?? 0,
        categoryName: category.name,
        amount: current?.amount,
        fromDate: current?.fromDate,
        isCurrent: current?.isCurrent,
      } as TimeReportingProjectFormTypes.SelfBillingRate;
    });
  };

  // A category can hold a queued change per month, so the row key has to carry the month too —
  // the category name alone collides and the table would render two rows under one key.
  const selfBillingRowKey = (categoryId: number, fromDate?: Date) => {
    if (!fromDate) return `${categoryId}`;
    const month = new Date(fromDate);
    return `${categoryId}-${month.getFullYear()}-${month.getMonth() + 1}`;
  };

  // Rates that have not started yet — the "Köade förändringar" table.
  const mapSelfBillingFutureRates = (existingRates?: TimeReportingProjectFormTypes.SelfBillingRate[]) =>
    (existingRates ?? [])
      .filter((r) => r.fromDate && DateHelper.isBefore(new Date(), new Date(r.fromDate)))
      .map((r) => ({
        ...r,
        categoryName:
          r.categoryName ||
          (project?.projectCategories?.find((c) => c.id === r.timeReportingProjectCategoryId)?.name ?? ""),
        rowKey: selfBillingRowKey(r.timeReportingProjectCategoryId, r.fromDate),
      })) as TimeReportingProjectFormTypes.SelfBillingRate[];

  const initialValues: TimeReportingProjectFormTypes.ProjectMember = {
    isNew: props.isNew ?? true,
    name: props.name ?? "",
    id: props.id ?? 0,
    startDate: (props.startDate ?? project.projectStartDate) as Date,
    endDate: props.endDate,
    isTimeReportingActive: props.isTimeReportingActive,
    categories: mapExtendedCategories(
      props.project?.projectCategoryMembers ?? [],
      props.project?.projectCategories ?? [],
      props.id ?? 0
    ),
    categoryRates: mapCategoryRates(),
    futureCategoryRates: mapFutureCategoryRates(),
    selfBillingEnabled: props.selfBillingEnabled ?? false,
    selfBillingOrgNumber: props.selfBillingOrgNumber,
    selfBillingEmail: props.selfBillingEmail,
    selfBillingPaymentMethod: props.selfBillingPaymentMethod,
    selfBillingBankgiro: props.selfBillingBankgiro,
    selfBillingPlusgiro: props.selfBillingPlusgiro,
    selfBillingClearingNumber: props.selfBillingClearingNumber,
    selfBillingAccountNumber: props.selfBillingAccountNumber,
    selfBillingIBAN: props.selfBillingIBAN,
    selfBillingPaymentTerms: props.selfBillingPaymentTerms,
    selfBillingCalculationMethod: props.selfBillingCalculationMethod,
    selfBillingPercentage: props.selfBillingPercentage,
    // Submitting the dialog replaces the member's selfBillingHourlyRates with the collapsed
    // one-row-per-category list, holding the queued amount under the current row's id. Deriving
    // the visible row from that collapsed list would then show the queued amount as the rate in
    // force and use it as the change baseline when the member is reopened before the project is
    // saved. selfBillingAllHourlyRates keeps the full history, so use it once it exists and only
    // fall back to selfBillingHourlyRates the first time the member is opened.
    selfBillingHourlyRates: mapSelfBillingRates(
      props.selfBillingAllHourlyRates ?? props.selfBillingHourlyRates,
      mapExtendedCategories(
        props.project?.projectCategoryMembers ?? [],
        props.project?.projectCategories ?? [],
        props.id ?? 0
      )
    ),
    selfBillingAllHourlyRates: props.selfBillingAllHourlyRates ?? props.selfBillingHourlyRates ?? [],
    selfBillingFutureRates: props.selfBillingFutureRates ?? mapSelfBillingFutureRates(props.selfBillingHourlyRates),
    selfBillingChangesApplyFrom: undefined,
    selfBillingMonthlyCost: props.selfBillingMonthlyCost,
  };

  const searchPersonsAutocomplete = async (searchStr: string) => {
    const result = await timeReportingProjectService.searchAvailableProjectMembersAutoComplete(
      searchStr,
      excludeMemberIds
    );
    return result;
  };

  const onProjectMemberChange = (
    name: string,
    updatedValue: any,
    formikProps: FormikProps<TimeReportingProjectFormTypes.ProjectMember>,
    userInput?: boolean | undefined
  ) => {};

  const handleSubmit = (values: TimeReportingProjectFormTypes.ProjectMember) =>
    props.handleSubmit(stripInactiveSelfBillingAccountFields(values));

  const onMemberDatesChange = (
    name: string,
    updatedValue: Date,
    formikProps: FormikProps<TimeReportingProjectFormTypes.ProjectMember>
  ) => {
    const { values, setFieldValue } = formikProps;
    const isActive = DateHelper.withinIntervall(new Date(), values.startDate, values.endDate);
    setFieldValue(FormFieldConstants.TimeReportingProject.ProjectMember.IsTimeReportingActive, isActive);
  };

  const onCategorySelectAllActivationChange = (
    name: string,
    updatedValue: TimeReportingProjectFormTypes.ExtendedProjectCategory[]
  ) => {
    const values: TimeReportingProjectFormTypes.ProjectMember =
      formRef.current?.getValues && formRef.current.getValues();

    const newValues = [...updatedValue];
    const newCategoryRates: TimeReportingProjectFormTypes.CategoryRate[] = [];
    const categories: TimeReportingProjectFormTypes.ExtendedProjectCategory[] = [];

    newValues.forEach((nv) => {
      let category = { ...nv };
      if (nv.selected) {
        const existingCategoryRate = values?.categoryRates?.find((cr) => cr.name === nv.name);
        if (existingCategoryRate) {
          newCategoryRates.push(existingCategoryRate);
        } else {
          const categoryRate = getCategoryRateOnSelectionChange(nv, values);
          categoryRate && newCategoryRates.push(categoryRate);
          category = {
            ...category,
            individualBillingModel: categoryRate?.individualBillingModel,
            hourlyRate: categoryRate.hourlyRate,
            hours: categoryRate.hours,
          };
        }
      } else {
        const defaultHourlyRate = project?.projectCategoryMembers
          ?.find((pcm) => pcm.projectCategory?.name === category.name && pcm.projectMember?.id === props.id)
          ?.hourlyRates?.find((hr) => !hr.fromDate);
        if (!defaultHourlyRate) {
          category = { ...category, individualBillingModel: undefined };
        } else {
          category = { ...category, accessUntil: category.accessUntil ?? DateHelper.getLastDayOfMonth(new Date()) };
        }
      }
      categories.push(category);
    });

    formRef.current?.setValues &&
      formRef.current.setValues({ ...values, categoryRates: [...newCategoryRates], categories: [...categories] });
  };

  const onCategoryActivationChange = (
    name: string,
    updatedValue: boolean,
    formikProps: FormikProps<TimeReportingProjectFormTypes.ProjectMember>,
    userInput?: boolean
  ) => {
    if (!userInput) {
      return;
    }
    const { values } = formikProps;

    const fieldInfo = FormHelper.getTableRowFieldInfo(name);

    const newValues = values.categories?.map((c, index) =>
      index === fieldInfo?.rowIndex ? { ...c, selected: updatedValue } : c
    );

    onCategorySelectAllActivationChange(FormFieldConstants.TimeReportingProject.ProjectMember.Categories, [
      ...(newValues ?? []),
    ]);
  };

  const getCategoryRateOnSelectionChange = (
    category: TimeReportingProjectFormTypes.ExtendedProjectCategory,
    member?: TimeReportingProjectFormTypes.ProjectMember
  ) => {
    const existingMemberRelation = project?.projectCategoryMembers?.find(
      (pcm) => pcm.projectCategory?.name === category.name && pcm.projectMember?.id === props.id
    );
    if (existingMemberRelation) {
      return {
        ...existingMemberRelation.projectCategory,
        hours: existingMemberRelation.normalDebitHours,
        individualBillingModel: existingMemberRelation.individualBillingModel,
        individualBillingModelName: timeReportingProjectService.getMemberBillingModelText(
          existingMemberRelation.individualBillingModel
        ),
        hourlyRate:
          existingMemberRelation.hourlyRates?.find((hr) => !hr.fromDate)?.hourlyRate ??
          existingMemberRelation.normalDebitHours,
      } as TimeReportingProjectFormTypes.CategoryRate;
    }

    const freshCategory = project?.projectCategories?.find((pc) => pc.name === category.name);
    return {
      ...freshCategory,
      hours: 0,
      hourlyRate: category?.defaultHourlyRate,
      individualBillingModel: 0,
      individualBillingModelName: timeReportingProjectService.getMemberBillingModelText(0),
      selected: props.isNew ?? true,
    } as TimeReportingProjectFormTypes.CategoryRate;
  };

  const onIndividualBillingModelChange = (
    item: AutocompleteListItem,
    name: string,
    formikProps: FormikProps<TimeReportingProjectFormTypes.ProjectMember>
  ) => {
    const { values, setFieldValue } = formikProps;
    const fieldInfo = FormHelper.getTableRowFieldInfo(name);
    const categoryRate = values.categoryRates?.find((cr, index) => index === fieldInfo?.rowIndex);
    let debitHours = categoryRate?.hours ?? 0;

    if (Number(item.id) !== ApiModel.TimeReportingPersonProjectBillingModel.Hourly) {
      debitHours = TimeReportingConstants.NormalHoursPerMonth;
    }

    const updatedMemberCategories =
      values.categories?.map((c) =>
        // eslint-disable-next-line eqeqeq --- supressed to avoid regression testing if changed
        c.name == categoryRate?.name ? { ...c, individualBillingModel: Number(item.id), hours: debitHours } : c
      ) ?? [];

    const futureCategoryRates =
      values.futureCategoryRates?.map((fcr) =>
        fcr.name === categoryRate?.name ? { ...fcr, individualBillingModel: Number(item.id), hours: debitHours } : fcr
      ) ?? [];

    addFutureRates(futureCategoryRates, values, categoryRate, Number(item.id));

    const updatedCategoryRates =
      values.categoryRates?.map((cr) =>
        cr.name === categoryRate?.name
          ? {
              ...cr,
              hours: debitHours,
              individualBillingModel: item.id,
              individualBillingModelName: timeReportingProjectService.getMemberBillingModelText(Number(item.id)),
            }
          : cr
      ) ?? [];

    setFieldValue(FormFieldConstants.TimeReportingProject.ProjectMember.CategoryRates, [...updatedCategoryRates]);
    setFieldValue(FormFieldConstants.TimeReportingProject.ProjectMember.FutureCategoryRates, [...futureCategoryRates]);
    setFieldValue(FormFieldConstants.TimeReportingProject.ProjectMember.Categories, [...updatedMemberCategories]);
  };

  const onHourlyRateChange = (
    name: string,
    updatedValue: any,
    formikProps: FormikProps<TimeReportingProjectFormTypes.ProjectMember>
  ) => {
    const { values, setFieldValue } = formikProps;
    const fieldInfo = FormHelper.getTableRowFieldInfo(name);

    // eslint-disable-next-line eqeqeq --- supressed to avoid regression testing if changed
    const categoryRate = values.categoryRates?.find((cr, index) => index == fieldInfo?.rowIndex);

    const updatedFutureCategoryRates = [...(values.futureCategoryRates ?? [])];

    if (values.changesApplyFrom) {
      const existingFutureRateIndex = values.futureCategoryRates?.findIndex(
        (fcr) =>
          fcr.name === categoryRate?.name &&
          fcr.validFrom &&
          values.changesApplyFrom &&
          DateHelper.isEqual(fcr.validFrom, values.changesApplyFrom)
      );
      if (existingFutureRateIndex !== undefined && existingFutureRateIndex > -1) {
        updatedFutureCategoryRates[existingFutureRateIndex].hourlyRate = updatedValue;
      } else {
        const newFutureCategoryRate = {
          ...categoryRate,
          validFrom: values.changesApplyFrom,
          hourlyRate: updatedValue,
        } as TimeReportingProjectFormTypes.FutureCategoryRate;
        updatedFutureCategoryRates.push(newFutureCategoryRate);
      }

      setFieldValue(FormFieldConstants.TimeReportingProject.ProjectMember.FutureCategoryRates, [
        ...updatedFutureCategoryRates,
      ]);
    }
    if (props.isNew ?? true) {
      const categories =
        values.categories?.map((c) =>
          c.name === categoryRate?.name
            ? { ...c, hourlyRate: updatedValue, individualBillingModel: categoryRate?.individualBillingModel }
            : c
        ) ?? [];
      setFieldValue(FormFieldConstants.TimeReportingProject.ProjectMember.Categories, [...categories]);
    }
  };

  const onCategoryRateHoursChange = (
    name: string,
    updatedValue: number,
    formikProps: FormikProps<TimeReportingProjectFormTypes.ProjectMember>,
    userInput?: boolean
  ) => {
    if (!userInput) {
      return;
    }

    const { values, setValues } = formikProps;
    const fieldInfo = FormHelper.getTableRowFieldInfo(name);

    // eslint-disable-next-line eqeqeq --- supressed to avoid regression testing if changed
    const categoryRate = values.categoryRates?.find((cr, index) => index == fieldInfo?.rowIndex);

    const futureCategoryRates =
      values.futureCategoryRates?.map((fcr) =>
        fcr.name === categoryRate?.name ? { ...fcr, hours: updatedValue } : fcr
      ) ?? [];

    addFutureRates(futureCategoryRates, values, categoryRate, categoryRate?.individualBillingModel, updatedValue);

    const updatedCategories =
      // eslint-disable-next-line eqeqeq --- supressed to avoid regression testing if changed
      values.categories?.map((c) => (c.name == categoryRate?.name ? { ...c, hours: updatedValue } : c)) ?? [];

    setValues({ ...values, futureCategoryRates: [...futureCategoryRates], categories: [...updatedCategories] });
  };

  const onChangesApplyMonthChange = (
    name: string,
    updatedValue: Date,
    formikProps: FormikProps<TimeReportingProjectFormTypes.ProjectMember>
  ) => {
    const { values, setValues } = formikProps;

    const newFutureRates: TimeReportingProjectFormTypes.FutureCategoryRate[] =
      values.futureCategoryRates?.map((fcr) => ({
        ...fcr,
        currentRate: values.categories?.find((c) => c.name === fcr.name)?.currentRate,
      })) ?? [];
    values.categoryRates
      ?.filter((cr) => cr.selected)
      .forEach((cr) => {
        const existingFutureRateIndex = values.futureCategoryRates?.findIndex(
          (fcr) => fcr.name === cr.name && fcr.validFrom && DateHelper.isEqual(fcr.validFrom, updatedValue)
        );
        if (existingFutureRateIndex !== undefined && existingFutureRateIndex > -1) {
          newFutureRates[existingFutureRateIndex].hourlyRate = cr.hourlyRate;
        } else {
          newFutureRates.push({ ...cr, validFrom: new Date(updatedValue) });
        }
      });

    const updatedCategories =
      values.categories?.map((c) =>
        values.categoryRates?.find((cr) => cr.name === c.name)?.selected
          ? ({ ...c, hourlyRate: c.currentRate } as TimeReportingProjectFormTypes.ExtendedProjectCategory)
          : c
      ) ?? [];

    setValues({ ...values, futureCategoryRates: [...newFutureRates], categories: [...updatedCategories] });
  };

  const changedSelfBillingRows = (rows?: TimeReportingProjectFormTypes.SelfBillingRate[]) =>
    rows?.filter((row) => {
      const initial = initialValues.selfBillingHourlyRates?.find(
        (i) => i.timeReportingProjectCategoryId === row.timeReportingProjectCategoryId
      );
      return initial !== undefined && initial.amount !== undefined && initial.amount !== row.amount;
    }) ?? [];

  // The rows this dialog queues are rebuilt from the edits that are on screen right now instead of
  // being added one by one, so changing your mind about the month or typing the original amount
  // back cannot leave a rate behind that the readonly table gives no way to remove. Rows queued
  // before the dialog opened are the baseline; a rebuilt row for the same category and month takes
  // one over, keeping its id so the save still treats it as an edit of that rate rather than a
  // second one — which the (category, from-date) index would reject.
  const buildSelfBillingFutureRates = (values: TimeReportingProjectFormTypes.ProjectMember) => {
    const alreadyQueued = initialValues.selfBillingFutureRates ?? [];
    const month = values.selfBillingChangesApplyFrom;
    if (!month) {
      return [...alreadyQueued];
    }

    const isForMonth = (rate: TimeReportingProjectFormTypes.SelfBillingRate) =>
      !!rate.fromDate && DateHelper.isEqual(new Date(rate.fromDate), month);

    const queued = changedSelfBillingRows(values.selfBillingHourlyRates).map((row) => {
      const queuedMatch = alreadyQueued.find(
        (r) => r.timeReportingProjectCategoryId === row.timeReportingProjectCategoryId && isForMonth(r)
      );
      // The chosen month can also be the month the currently active rate already started in —
      // minDate allows picking it to correct that rate. It carries no queued row of its own to
      // match above, so without reusing its own id here the save would carry two rows for the
      // same category and day, which Core rejects as a duplicate.
      const startedRowId = !!row.fromDate && DateHelper.isEqual(new Date(row.fromDate), month) ? row.id : undefined;
      return {
        id: queuedMatch?.id ?? startedRowId,
        timeReportingProjectCategoryId: row.timeReportingProjectCategoryId,
        categoryName: row.categoryName,
        amount: row.amount,
        fromDate: new Date(month),
        isCurrent: false,
        rowKey: selfBillingRowKey(row.timeReportingProjectCategoryId, month),
      };
    });

    const replacedCategories = new Set(queued.map((r) => r.timeReportingProjectCategoryId));
    const kept = alreadyQueued.filter(
      (r) => !(isForMonth(r) && replacedCategories.has(r.timeReportingProjectCategoryId))
    );
    return [...kept, ...queued];
  };

  const onSelfBillingQueuedRateChange = (
    name: string,
    updatedValue: any,
    formikProps: FormikProps<TimeReportingProjectFormTypes.ProjectMember>
  ) => {
    const { values, setFieldValue } = formikProps;
    // Without a stored rate there is nothing to change from; the amount is the original rate.
    if (!hasSelfBillingRateHistory) {
      return;
    }
    setFieldValue(
      FormFieldConstants.TimeReportingProject.ProjectMember.SelfBillingFutureRates,
      buildSelfBillingFutureRates(values)
    );
  };

  const addFutureRates = (
    futureCategoryRates: TimeReportingProjectFormTypes.FutureCategoryRate[],
    values: TimeReportingProjectFormTypes.ProjectMember,
    categoryRate?: TimeReportingProjectFormTypes.CategoryRate,
    individualBillingModel?: ApiModel.TimeReportingPersonProjectBillingModel,
    debitHours?: number
  ) => {
    if (values.changesApplyFrom) {
      const existingFutureRate = values.futureCategoryRates?.find(
        (fcr) =>
          fcr.name === categoryRate?.name &&
          fcr.validFrom &&
          values.changesApplyFrom &&
          DateHelper.isEqual(fcr.validFrom, values.changesApplyFrom)
      );
      if (!existingFutureRate) {
        const newFutureCategoryRate = {
          ...categoryRate,
          validFrom: values.changesApplyFrom,
          hours: debitHours ?? categoryRate?.hours,
          individualBillingModel: individualBillingModel ?? categoryRate?.individualBillingModel,
        } as TimeReportingProjectFormTypes.FutureCategoryRate;
        futureCategoryRates.push(newFutureCategoryRate);
      }
    }
  };

  return (
    <TimeReportingProjectMemberForm
      ref={formRef}
      {...props}
      isNew={props.id === undefined}
      hasSelfBillingRateHistory={hasSelfBillingRateHistory}
      validationSchema={validationSchema}
      onSearchPersonsAutoComplete={searchPersonsAutocomplete}
      defaultValues={initialValues}
      handleSubmit={handleSubmit}
      isInternalProject={isInternalProject}
      onProjectMemberChange={onProjectMemberChange}
      earliestStartDate={earliestStartDate}
      latestEndDate={latestEndDate}
      onDatesChange={onMemberDatesChange}
      onCategoryActivationChange={onCategoryActivationChange}
      onCategorySelectAllActivationChange={onCategorySelectAllActivationChange}
      onIndividualBillingModelChange={onIndividualBillingModelChange}
      onHourlyRateChange={onHourlyRateChange}
      onCategoryRateHoursChange={onCategoryRateHoursChange}
      onChangesApplyMonthChange={onChangesApplyMonthChange}
      onSelfBillingAmountChange={onSelfBillingQueuedRateChange}
      onSelfBillingChangesApplyMonthChange={onSelfBillingQueuedRateChange}
    />
  );
};

export default forwardRef(TimeReportingProjectMemberFormContainer);
